import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playAlertBeep } from "@/lib/beep";

const POLL_MS = 20000;

// Same polling/watermark pattern as useInquiryAlerts, for leads submitted through the
// unlisted ad-campaign landing page (AdPromoPage). Sound alerts share one global
// AudioContext primed elsewhere on the dashboard (see useInquiryAlerts' armSound),
// so this hook just plays the beep — no separate arm button needed.
export function useAdLeadAlerts(user) {
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);

  const lastSeenRef = useRef(null);
  const initializedRef = useRef(false);
  const storageKey = user ? `eazy_last_seen_ad_lead_${user.email}` : null;

  useEffect(() => {
    if (!storageKey) return;
    lastSeenRef.current = localStorage.getItem(storageKey);
    initializedRef.current = false;
  }, [storageKey]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get("/admin/ad-leads");
        if (cancelled) return;
        const items = data.items || [];
        setLeads(items);

        if (items.length > 0) {
          const newest = items[0].created_at;

          if (!initializedRef.current) {
            initializedRef.current = true;
            if (!lastSeenRef.current) {
              lastSeenRef.current = newest;
              if (storageKey) localStorage.setItem(storageKey, newest);
            }
          } else {
            const freshOnes = lastSeenRef.current
              ? items.filter((l) => l.created_at > lastSeenRef.current)
              : [];
            if (freshOnes.length > 0) {
              freshOnes.forEach((lead) => {
                toast.success(`New ad lead from ${lead.name}`, { description: lead.email });
              });
              playAlertBeep();
              lastSeenRef.current = newest;
              if (storageKey) localStorage.setItem(storageKey, newest);
            }
          }
        }
      } catch {
        // silent — avoid spamming errors from background polling
      } finally {
        if (!cancelled) setLoadingLeads(false);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user, storageKey]);

  return { leads, setLeads, loadingLeads };
}
